import React, { useState, useRef, useEffect, useMemo } from "react";

import type { DropdownInputOption } from "types/DropdownInputOption";
import ArrowUpIcon from "assets/icons/ArrowUpIcon";
import ArrowDownIcon from "assets/icons/ArrowDownIcon";
import CancelIcon from "assets/icons/CancelIcon";

const getAcademicYear = (label: string) => {
  const matchedYear = label.match(/\b(19|20)\d{2}\b/);

  return matchedYear ? Number(matchedYear[0]) : null;
};

const sortDropdownOptions = (dropdownOptions: DropdownInputOption[]) => {
  return [...dropdownOptions].sort((a, b) => {
    const yearA = getAcademicYear(a.label);
    const yearB = getAcademicYear(b.label);

    if (yearA !== null && yearB !== null && yearA !== yearB) {
      return yearB - yearA;
    }

    if (yearA !== null && yearB === null) {
      return -1;
    }

    if (yearA === null && yearB !== null) {
      return 1;
    }

    return a.label.localeCompare(b.label);
  });
};

interface MultipleValueDropdownInputProps {
  placeholder: string;
  options: DropdownInputOption[];
  value: string[];
  disable?: boolean;
  isLoading?: boolean;
  loadingMessage?: string;
  emptyMessage?: string;
  onChange: (value: string[]) => void;
  maxHeight?: string;
}

const MultipleValueDropdownInput: React.FC<MultipleValueDropdownInputProps> = ({
  placeholder,
  options,
  value,
  disable,
  isLoading = false,
  loadingMessage = "Loading...",
  emptyMessage = "No options available",
  onChange,
  maxHeight = "36rem",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const sortedOptions = useMemo(() => {
    return sortDropdownOptions(options);
  }, [options]);
  const selectableValues = useMemo(() => {
    return sortedOptions
      .filter((option) => value.includes(option.value))
      .map((option) => option.value);
  }, [sortedOptions, value]);
  const availableOptions = useMemo(() => {
    return sortedOptions.filter((option) => !value.includes(option.value));
  }, [sortedOptions, value]);
  const sortSelectedValues = (selectedValues: string[]) => {
    return sortedOptions
      .filter((option) => selectedValues.includes(option.value))
      .map((option) => option.value);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div
      ref={dropdownRef}
      className={`w-full space-y-1 relative cursor-pointer ${
        disable ? "bg-white/50 text-[#666666]" : ""
      }`}
    >
      <div
        className="w-full flex items-stretch bg-white rounded-lg border border-[#9B9B9B] overflow-hidden"
        onClick={() => !disable && setIsOpen(!isOpen)}
      >
        <div className="flex min-w-0 flex-1 flex-wrap items-center px-3 py-2">
          {selectableValues.length > 0 ? (
            selectableValues.map((v) => (
              <div
                key={v}
                className="m-1 flex items-center justify-between gap-2 rounded bg-[#E6E6E6] p-1 transition-colors duration-200 ease-out hover:bg-[#D6D6D6]"
              >
                {options.find((option) => option.value === v)?.label}
                <button
                  type="button"
                  className="h-5 w-5 rounded bg-white transition-colors duration-200 ease-out hover:bg-[#F4F4F4]"
                  onClick={(e) => {
                    e.stopPropagation();
                    onChange(sortSelectedValues(value.filter((va) => va !== v)));
                  }}
                >
                  <CancelIcon
                    width={"100%"}
                    height={"100%"}
                    color={"#767676"}
                  />
                </button>
              </div>
            ))
          ) : (
            <p>{isLoading ? loadingMessage : placeholder}</p>
          )}
        </div>
        <div className="dropdown-chevron-segment shrink-0">
          <span
            aria-hidden="true"
            className="dropdown-chevron-divider"
          />
          {isOpen ? (
            <ArrowUpIcon width={24} height={24} color={"#767676"} />
          ) : (
            <ArrowDownIcon width={24} height={24} color={"#767676"} />
          )}
        </div>
      </div>
      {isOpen && (
        <ul style={{ maxHeight: maxHeight }} className="w-full bg-white rounded-lg p-2 overflow-auto absolute z-40 list-none shadow-xl border border-[#9B9B9B]">
          {availableOptions.length > 0 ? (
            availableOptions.map((option) => (
              <li
                key={option.value}
                className="p-2 cursor-pointer hover:bg-[#F4F4F4]"
                onClick={() => {
                  onChange(sortSelectedValues([...value, option.value]));
                  setIsOpen(false);
                }}
              >
                {option.label}
              </li>
            ))
          ) : (
            <li className="p-2 text-[#767676]">
              {isLoading ? loadingMessage : emptyMessage}
            </li>
          )}
        </ul>
      )}
    </div>
  );
};

export default MultipleValueDropdownInput;
